import React, { useState, useEffect } from 'react';
import chatService from '../services/chatService';

const RatingDetailsModal = ({ isOpen, onClose }) => {
  const [ratingStats, setRatingStats] = useState(null);
  const [recentRatings, setRecentRatings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats'); // 'stats' or 'reviews'

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);

    // Подписываемся на статистику
    const unsubscribe = chatService.subscribeToRatingStats((data) => {
      setRatingStats(data);
      setIsLoading(false);
    });

    // Загружаем последние отзывы
    chatService.getAllRatings().then((ratings) => {
      // Фильтруем только те, у которых есть feedback
      const withFeedback = ratings.filter(r => r.feedback && r.feedback.trim());
      setRecentRatings(withFeedback.slice(0, 10)); // Последние 10
    });

    return () => {
      unsubscribe();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getRatingPercentage = (rating) => {
    if (!ratingStats || !ratingStats.distribution) return 0;
    const count = ratingStats.distribution[rating] || 0;
    return ratingStats.count > 0 ? Math.round((count / ratingStats.count) * 100) : 0;
  };

  // eslint-disable-next-line no-unused-vars
  const getStarColor = (rating) => {
    if (rating >= 4.5) return 'text-green-500';
    if (rating >= 3.5) return 'text-yellow-500';
    if (rating >= 2.5) return 'text-orange-500';
    return 'text-red-500';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Недавно';
    const date = new Date(timestamp);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getRatingEmoji = (rating) => {
    const emojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '🤩' };
    return emojis[rating] || '⭐';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scale-in border border-gray-100">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Рейтинг сервиса</h2>
                  <p className="text-sm text-gray-500">Отзывы наших пользователей</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 mt-5">
              <button
                onClick={() => setActiveTab('stats')}
                className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === 'stats'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Статистика
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === 'reviews'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Отзывы ({recentRatings.length})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] bg-gray-50">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {/* Stats Tab */}
                {activeTab === 'stats' && ratingStats && (
                  <div className="space-y-6">
                    {/* Overall Rating */}
                    <div className="bg-white rounded-lg p-6 text-center border border-gray-200 shadow-sm">
                      <div className="text-5xl font-bold text-gray-900 mb-2">
                        {parseFloat(ratingStats.average).toFixed(1)}
                      </div>
                      <div className="flex justify-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg
                            key={star}
                            className={`w-5 h-5 ${
                              star <= Math.round(parseFloat(ratingStats.average))
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300 fill-current'
                            }`}
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600">
                        На основе <strong>{ratingStats.count}</strong> отзывов
                      </p>
                    </div>

                    {/* Rating Distribution */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="font-semibold text-gray-800 mb-4">Распределение оценок</h3>
                      <div className="space-y-3">
                        {[5, 4, 3, 2, 1].map((rating) => {
                          const count = ratingStats.distribution[rating] || 0;
                          const percentage = getRatingPercentage(rating);
                          return (
                            <div key={rating} className="flex items-center gap-3">
                              <div className="flex items-center gap-1 w-14">
                                <span className="text-sm font-medium text-gray-700">{rating}</span>
                                <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                              </div>
                              <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-500"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600 w-20 text-right">
                                {count} ({percentage}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <div className="space-y-3">
                    {recentRatings.length === 0 ? (
                      <div className="bg-white rounded-lg p-12 text-center border border-gray-200">
                        <div className="text-4xl mb-3">💬</div>
                        <p className="text-gray-600 font-medium">Пока нет текстовых отзывов</p>
                        <p className="text-sm text-gray-500 mt-1">Будьте первым, кто оставит отзыв!</p>
                      </div>
                    ) : (
                      recentRatings.map((review, index) => (
                        <div
                          key={review.id || index}
                          className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-sm transition-all animate-fade-in-up"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{getRatingEmoji(review.rating)}</span>
                              <div className="flex gap-0.5">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <svg
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= review.rating
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300 fill-current'
                                    }`}
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                  </svg>
                                ))}
                              </div>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(review.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {review.feedback}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingDetailsModal;

